import { isDefined } from "@clipboard-health/util-ts";
import { useSkillsAssessmentFeatureFlags } from "@src/appV2/SkillsAssessment/api/useSkillsAssessmentFeatureFlags";
import { useSkillsAssessments } from "@src/appV2/SkillsAssessment/api/useSkillsAssessments";
import { SkillsAssessmentWorkerStatus } from "@src/appV2/SkillsAssessment/types";
import { isAfter, parseISO } from "date-fns";

import { formatOrientationDateTime } from "../utils";

interface Params {
  workerId: string;
}

function getYetToScheduleBannerText() {
  return "Complete your Demo Shift in order to book shifts at most workplaces!";
}

function getScheduledForFutureBannerText(date: string) {
  return `Your Demo Shift is scheduled for ${formatOrientationDateTime(date)}`;
}

function getStartOrientationBannerText() {
  return "It's time for your Demo Shift!";
}

function getMissedOrientationBannerText() {
  return "You missed your Demo Shift";
}

export function useOrientationBannerText(params: Params): {
  orientationBannerText: string | undefined;
  isOrientationBannerTextLoading: boolean;
} {
  const { workerId } = params;
  const { isClipboardOrientationEnabled } = useSkillsAssessmentFeatureFlags();

  const {
    data: assessments,
    isSuccess: isSuccessAssessments,
    isLoading: isLoadingAssessments,
  } = useSkillsAssessments(
    {
      workerId,
    },
    { staleTime: 0, enabled: isClipboardOrientationEnabled }
  );

  if (!isClipboardOrientationEnabled) {
    return { orientationBannerText: undefined, isOrientationBannerTextLoading: false };
  }

  if (isLoadingAssessments) {
    return { orientationBannerText: undefined, isOrientationBannerTextLoading: true };
  }

  if (!isSuccessAssessments || assessments.data.length === 0) {
    return { orientationBannerText: undefined, isOrientationBannerTextLoading: false };
  }

  const assessmentStatus = assessments.included.find(
    (included) => included.type === "assessment-status"
  );
  const assessmentScheduledEvent = assessments.included.find(
    (included) => included.type === "assessment-event"
  );

  const { status, isAllowedToStartAssessment } = assessmentStatus?.attributes ?? {};
  const { scheduledFor } = assessmentScheduledEvent?.attributes ?? {};

  if (status === SkillsAssessmentWorkerStatus.PENDING) {
    if (isDefined(scheduledFor)) {
      return {
        orientationBannerText:
          (isAllowedToStartAssessment ?? isAfter(new Date(), parseISO(scheduledFor)))
            ? getStartOrientationBannerText()
            : getScheduledForFutureBannerText(scheduledFor),
        isOrientationBannerTextLoading: false,
      };
    }

    return {
      orientationBannerText: getYetToScheduleBannerText(),
      isOrientationBannerTextLoading: false,
    };
  }

  if (status === SkillsAssessmentWorkerStatus.FAILED) {
    return {
      orientationBannerText: getMissedOrientationBannerText(),
      isOrientationBannerTextLoading: false,
    };
  }

  return {
    orientationBannerText: undefined,
    isOrientationBannerTextLoading: false,
  };
}
